import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { roundToTwoDecimals } from "../utils/transactionutils";

const INITIAL_TOTALS = {
  totalCharges: 0,
  totalDisbursement: 0,
};

const INITIAL_STATE = {
  paymentChannel: null,
  merchantBalance: null,
  entries: [],
  editingId: null,
  runningTotals: { ...INITIAL_TOTALS },
  phoneVerificationStatus: "idle",
  verifiedPhone: null,
  verifiedName: null,
  phoneVerificationMessage: null,
  bankVerificationStatus: "idle",
  verifiedAccountNumber: null,
  verifiedBankCode: null,
  verifiedBankName: null,
  bankVerificationMessage: null,
};

const useManualEntryStore = create(
  persist(
    (set) => ({
      ...INITIAL_STATE,

      setPaymentChannel: (channel) => set({ paymentChannel: channel }),

      setMerchantBalance: (balance) => set({ merchantBalance: balance }),

      addEntry: (entry) =>
        set((state) => ({
          entries: [...state.entries, entry],
          runningTotals: {
            totalCharges: roundToTwoDecimals(state.runningTotals.totalCharges + (entry.charges ?? 0)),
            totalDisbursement: roundToTwoDecimals(state.runningTotals.totalDisbursement + (entry.amount ?? 0)),
          },
        })),

      updateEntry: (id, newEntry) =>
        set((state) => {
          const old = state.entries.find((e) => e.id === id);
          return {
            entries: state.entries.map((e) => (e.id === id ? { ...newEntry, id } : e)),
            runningTotals: {
              totalCharges: roundToTwoDecimals(
                state.runningTotals.totalCharges - (old?.charges ?? 0) + (newEntry.charges ?? 0),
              ),
              totalDisbursement: roundToTwoDecimals(
                state.runningTotals.totalDisbursement - (old?.amount ?? 0) + (newEntry.amount ?? 0),
              ),
            },
          };
        }),

      removeEntry: (id) =>
        set((state) => {
          const entry = state.entries.find((e) => e.id === id);
          const remaining = state.entries.filter((e) => e.id !== id);
          return {
            entries: remaining,
            merchantBalance: remaining.length === 0 ? null : state.merchantBalance,
            runningTotals: {
              totalCharges: roundToTwoDecimals(state.runningTotals.totalCharges - (entry?.charges ?? 0)),
              totalDisbursement: roundToTwoDecimals(
                state.runningTotals.totalDisbursement - (entry?.amount ?? 0),
              ),
            },
          };
        }),

      setEditing: (id, { phone, name, accountNumber, bankCode, bankName } = {}) =>
        set({
          editingId: id,
          phoneVerificationStatus: phone ? "verified" : "idle",
          verifiedPhone: phone ?? null,
          verifiedName: name ?? null,
          phoneVerificationMessage: null,
          bankVerificationStatus: accountNumber ? "verified" : "idle",
          verifiedAccountNumber: accountNumber ?? null,
          verifiedBankCode: bankCode ?? null,
          verifiedBankName: bankName ?? null,
          bankVerificationMessage: null,
        }),

      clearEditing: () =>
        set({
          editingId: null,
          phoneVerificationStatus: "idle",
          verifiedPhone: null,
          verifiedName: null,
          phoneVerificationMessage: null,
          bankVerificationStatus: "idle",
          verifiedAccountNumber: null,
          verifiedBankCode: null,
          verifiedBankName: null,
          bankVerificationMessage: null,
        }),

      setPhoneVerification: ({ status, phone, name, message }) =>
        set({
          phoneVerificationStatus: status,
          verifiedPhone: phone,
          verifiedName: name ?? null,
          phoneVerificationMessage: message ?? null,
        }),

      resetPhoneVerification: () =>
        set({
          phoneVerificationStatus: "idle",
          verifiedPhone: null,
          verifiedName: null,
          phoneVerificationMessage: null,
        }),

      setBankVerification: ({ status, accountNumber, bankCode, bankName, message }) =>
        set({
          bankVerificationStatus: status,
          verifiedAccountNumber: accountNumber ?? null,
          verifiedBankCode: bankCode ?? null,
          verifiedBankName: bankName ?? null,
          bankVerificationMessage: message ?? null,
        }),

      resetBankVerification: () =>
        set({
          bankVerificationStatus: "idle",
          verifiedAccountNumber: null,
          verifiedBankCode: null,
          verifiedBankName: null,
          bankVerificationMessage: null,
        }),

      applyRowErrors: (errorRecipients) =>
        set((state) => {
          const updated = [...state.entries];
          errorRecipients.forEach(({ errorRowNo, errors }) => {
            const idx = errorRowNo - 1;
            if (updated[idx]) {
              updated[idx] = { ...updated[idx], errors };
            }
          });
          return { entries: updated };
        }),

      clearEntryErrors: () =>
        set((state) => ({
          entries: state.entries.map(({ errors: _errors, ...rest }) => rest),
        })),

      reset: () => set(INITIAL_STATE),
    }),
    {
      name: "manual-entry-store",
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        paymentChannel: state.paymentChannel,
        merchantBalance: state.merchantBalance,
        entries: state.entries,
        runningTotals: state.runningTotals,
        phoneVerificationStatus: state.phoneVerificationStatus,
        verifiedPhone: state.verifiedPhone,
        verifiedName: state.verifiedName,
        phoneVerificationMessage: state.phoneVerificationMessage,
        bankVerificationStatus: state.bankVerificationStatus,
        verifiedAccountNumber: state.verifiedAccountNumber,
        verifiedBankCode: state.verifiedBankCode,
        verifiedBankName: state.verifiedBankName,
        bankVerificationMessage: state.bankVerificationMessage,
      }),
    },
  ),
);

export default useManualEntryStore;
